import * as Sentry from '@sentry/react';
import dayjs from 'dayjs';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { JSONParser } from '@streamparser/json';

import { KEY_MYGPT_TOKEN, KEY_USER_FINGERPRINT, KEY_USER_NICKNAME } from '@mygpt/app/apis';
import { questions as questionsRequest } from '@mygpt/app/chat/services';
import {
  ClarificationType,
  FaqChildNode,
  Footnote,
  PageInfo,
  QuestionType,
  RecommendList,
  SessionListItem,
  SessionListParam,
} from '@mygpt/types';

import { QUESTION_TYPE, QuestionResponseFormat } from '../constant';
import { useQuestionSuggestion } from '@mygpt/app/chat/stores/useQuestionSuggestion';
import { getStorageValue } from '@mygpt/app/utils';

const QUESTION_TYPE_SIMPLE = QUESTION_TYPE.SIMPLE as QuestionType;

interface Props {
  aiId: string;
  sessionId: string;
  questionType?: QuestionType;
  messageId: string;
  showPrompt?: boolean;
  // 记录问答时当前页面信息
  pageInfo?: PageInfo;
  language?: string;
  agentMode?: string;
  userIdentifier?: string;
}

const splitSource = (text: string) => {
  const splitWord = '$SOURCES$:';
  const splitArr = text.split(splitWord);
  const oriText = splitArr[0];
  let sourceArr = (splitArr[1] || '').split(',');
  sourceArr = sourceArr.map((item) => item.trim()).filter((item) => item !== '');
  return {
    oriText,
    sourceArr,
  };
};

enum StreamingMessageDataType {
  REFERENCE_LIST = 'REFERENCE_LIST',
  REFERENCE_SIGN = 'REFERENCE_SIGN',
  PLAIN_TEXT = 'PLAIN_TEXT',
  FINISH = 'FINISH',
  ERROR = 'ERROR',
  LLM_CAN_ANSWER = 'LLM_CAN_ANSWER',
  FAQ_CHILD_NODE = 'FAQ_CHILD_NODE', // FAQ 问题树
  RECOMMEND_LIST = 'RECOMMEND_LIST', // 相关问题推荐
  SHOW_REFERENCE_BUTTON = 'SHOW_REFERENCE_BUTTON', // 展示引用按钮
  CLARIFICATION = 'CLARIFICATION', // 澄清类型。
}
interface MessageResponse {
  message_type: StreamingMessageDataType;
  message_id: string;
  content: unknown;
  use_faq: boolean;
  tokens: number;
}

interface Reference extends Footnote {}

export interface FetchQuestionOptions {
  faqID?: string;
  postAnswerProcessing?: (question: string, message: SessionListParam) => void;
}

const QuestionMessageErrorPrefix = 'Message_Error_';

export const useQuestion = ({
  aiId: id,
  messageId,
  pageInfo,
  questionType = QUESTION_TYPE.SIMPLE as QuestionType,
  sessionId,
  showPrompt,
  language,
  agentMode,
  userIdentifier,
}: Props) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [sessionList, setSessionList] = useState<SessionListItem>({ messages: [] });
  const { questionSuggestions } = useQuestionSuggestion();

  const controllerRef = useRef<AbortController>();
  const forcedPauseRef = useRef(false);
  const questionHasRsp = useRef(false);

  const isWidgetPluginPage = useMemo(() => {
    const regex = /^\/widget\/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
    return regex.test(window.location.pathname);
  }, []);

  const logAnswerError = (msg: unknown) => {
    Sentry.captureMessage(`Chatbot回答异常 botId:${id}, info:${msg}`, 'error');
  };

  const pendingToAnswer = (postAnswerProcessing?: (question: string, message: SessionListParam) => void) => {
    setSessionList((state: SessionListItem) => {
      const text = state.pending ?? '';
      const { oriText, sourceArr } = splitSource(text);

      const message: SessionListParam = {
        isAnswer: true,
        text: oriText,
        footnotes: state.footnotes,
        reference_list: state.reference_list,
        reference_indexes: state.reference_indexes,
        recommend_list: state.recommend_list,
        faq_child_node: state.faq_child_node,
        source: sourceArr,
        messageId,
        llm_can_answer: state.llm_can_answer,
        clarification: state.clarification,
      };

      if (forcedPauseRef.current) {
        message.userAborted = true;
      }

      const question = state.messages.at(-1)?.text;
      if (question) {
        // 调用函数，进行一些后置处理逻辑
        postAnswerProcessing?.(question, message);
      }

      return {
        messages: [...state.messages, message],
        reference_list: undefined,
        footnotes: undefined,
        pending: undefined,
      };
    });
  };

  const tokenIsExpired = (): boolean => {
    const token = (Cookies.get(KEY_MYGPT_TOKEN()) || '').replace('Bearer ', '');
    if (!token) return true;
    const tokenDecoded = jwtDecode(token);
    if (!tokenDecoded || !tokenDecoded.exp) return true;
    return dayjs(tokenDecoded.exp * 1000).isBefore(dayjs());
  };

  const stopAnswer = () => {
    forcedPauseRef.current = true;

    if (!questionHasRsp.current) {
      controllerRef.current && controllerRef.current.abort();
    }
  };

  const fetchQuestion = async (value: string, { faqID, postAnswerProcessing }: FetchQuestionOptions = {}) => {
    if (!value) return;
    setLoading(true);
    setSessionList((state: SessionListItem) => ({
      ...state,
      messages: [
        ...state.messages,
        {
          isAnswer: false,
          text: value,
        },
      ],
    }));

    if (!id) return;

    controllerRef.current = new AbortController();
    forcedPauseRef.current = false;

    const HISMobilePhoneList = (window as any).hismobile_phone_list;
    const validPhoneList = Array.isArray(HISMobilePhoneList) && HISMobilePhoneList?.length;

    const dynamicBackground = [];
    if (validPhoneList) {
      dynamicBackground.push({
        description: `The correspondence between the user's phone number and the encrypted information is represented by the variables phone and encrypted_phone. phone represents the user's real phone number, while encrypted_phone represents the encrypted string of the user's real phone number.`,
        phone_mapper: HISMobilePhoneList,
      });
    }

    const param = {
      ai_id: id,
      session_id: sessionId,
      message_id: messageId,
      question: value,
      stream: !showPrompt,
      debug_with_messages: showPrompt,
      simple_question: questionType === QUESTION_TYPE_SIMPLE,
      with_source: true,
      needToken: !isWidgetPluginPage && !tokenIsExpired(),
      format: QuestionResponseFormat.JSON_AST,
      user_identifier:
        userIdentifier || getStorageValue(KEY_USER_NICKNAME) || getStorageValue(KEY_USER_FINGERPRINT) || '',
      ...(dynamicBackground.length ? { dynamic_background: dynamicBackground } : {}),
    };
    // 大屏模式才需要指定语言及模式
    if (language) Object.assign(param, { language });
    if (agentMode) Object.assign(param, { agent_mode: agentMode });

    if (faqID) Object.assign(param, { recommend_faq_id: faqID });
    if (pageInfo && (pageInfo.title || pageInfo.url)) Object.assign(param, { page_info: pageInfo });
    if (questionSuggestions)
      Object.assign(param, {
        metadata: questionSuggestions.map((item) => ({
          key: item.id,
          value: item.question,
          type: 'FAQ_QUESTION',
        })),
      });
    questionHasRsp.current = false;
    questionsRequest(param, controllerRef.current.signal)
      .then(async (response) => {
        const parser = new JSONParser({
          separator: '\n', // 以换行符作为分隔符来尝试解析 JSON
        });

        if (!response.body) {
          setLoading(false);
          return;
        }

        const decoder = new TextDecoder();
        const reader = response.body.getReader();
        const referenceList: Reference[] = [];
        questionHasRsp.current = true;

        // 测试环境功能
        if (showPrompt) {
          let responseVal = '';
          // eslint-disable-next-line
          while (true) {
            const { done, value } = await reader.read();
            if (done || forcedPauseRef.current) {
              const answerData = JSON.parse(responseVal);
              setSessionList((state: SessionListItem) => ({
                messages: [
                  ...state.messages,
                  { isAnswer: true, text: answerData.answer, prompt: answerData.messages[0]?.content },
                ],
                pending: undefined,
              }));
              break;
            }

            const text = decoder.decode(value, { stream: true });
            responseVal += text;
          }
        }

        const recommendList: RecommendList[] = [];
        const faqChildNode: FaqChildNode[] = [];
        let llm_can_answer: boolean = true;
        let clarification: ClarificationType | undefined;

        parser.onValue = ({ value: json, key }) => {
          // 如果有key, 说明解析到的是一个不完整的JSON的一部分，这种数据不处理，继续等待
          // 如果key为空，说明解析到的是一个完整的、解析好的JSON，可以直接使用。
          if (key !== undefined) return;

          let message: MessageResponse = {
            message_type: StreamingMessageDataType.PLAIN_TEXT,
          } as MessageResponse;

          message = json as any;

          if (message.message_type === StreamingMessageDataType.REFERENCE_LIST) {
            if (message.content) {
              for (const item of message.content as Reference[]) {
                if (item.source) {
                  referenceList.push(item);
                }
              }
            }
            return;
          } else if (message.message_type === StreamingMessageDataType.SHOW_REFERENCE_BUTTON) {
            if (message.content !== 'true' && !message.use_faq) {
              referenceList.splice(0, referenceList.length);
            }
            message.content = '';
          } else if (message.message_type === StreamingMessageDataType.LLM_CAN_ANSWER) {
            llm_can_answer = message.content === 'true' ? true : false;
            message.content = '';
          } else if (message.message_type === StreamingMessageDataType.RECOMMEND_LIST) {
            if (!message.content) return;
            recommendList.push(...(message.content as Array<RecommendList>));
            return;
          } else if (message.message_type === StreamingMessageDataType.FAQ_CHILD_NODE) {
            if (!message.content) return;
            faqChildNode.push(...(message.content as Array<FaqChildNode>));
            return;
          } else if (message.message_type === StreamingMessageDataType.ERROR) {
            throw new Error(`${QuestionMessageErrorPrefix}${message.content}`);
          } else if (message.message_type === StreamingMessageDataType.CLARIFICATION) {
            clarification = message.content as ClarificationType;
            // 情况内容，是为了走下面的 setSessionList 逻辑时，不会把值也显示出来
            message.content = '';
          }

          setSessionList((state: SessionListItem) => {
            const footnotes = state.footnotes ?? [];
            const newState = {
              ...state,
              footnotes,
              reference_list: referenceList,
              reference_indexes: [],
              recommend_list: recommendList,
              faq_child_node: faqChildNode,
              pending: (state.pending ?? '') + message.content,
              llm_can_answer,
              clarification,
            };
            return newState;
          });
        };

        if (!showPrompt) {
          // eslint-disable-next-line
          while (true) {
            const { done, value } = await reader.read();
            if (done || forcedPauseRef.current) {
              pendingToAnswer(postAnswerProcessing);
              setLoading(false);
              controllerRef.current && controllerRef.current.abort();
              break;
            }

            let text = decoder.decode(value, { stream: true });
            try {
              parser.write(text);
            } catch (err) {
              console.log(`[useQuestion] 解析JSON失败: ${err}`); // handler errors
            }
          }
        }
      })
      .catch((err: unknown) => {
        const error = (typeof err === 'string' ? new Error(err) : err) as Error;
        let sentrtErrorText = err;
        let errorMsg: string | undefined = JSON.stringify(err);

        if (error.message) {
          switch (true) {
            case error.message.startsWith('Failed to fetch'):
              errorMsg = t('errors.network.description');
              break;
            case error.message.startsWith(QuestionMessageErrorPrefix): {
              const [, errorMessage] = error.message.split(QuestionMessageErrorPrefix);
              errorMsg = t('errors.answer.description');
              sentrtErrorText = errorMessage;
              break;
            }
            case error.message.startsWith('The user aborted a request'):
              pendingToAnswer();
              return;
            default:
              break;
          }
        } else {
          errorMsg = t('errors.network.description');
        }

        logAnswerError(sentrtErrorText);
        setSessionList((state: SessionListItem) => ({
          messages: [...state.messages, { isAnswer: true, text: '', prompt: '', error: errorMsg }],
          footnotes: undefined,
          recommend_list: undefined,
          reference_list: undefined,
          faq_child_node: undefined,
          pending: undefined,
          llm_can_answer: undefined,
          clarification: undefined,
        }));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const clearSessionList = () => {
    setSessionList({ messages: [] });
  };

  const updateSessionList = (state: SessionListItem) => {
    setSessionList(() => ({
      messages: state.messages,
      footnotes: state.footnotes,
      recommend_list: state.recommend_list,
      reference_list: state.reference_list,
      faq_child_node: state.faq_child_node,
      reference_indexes: state.reference_indexes,
      pending: state.pending,
      llm_can_answer: state.llm_can_answer,
      clarification: state.clarification,
    }));
  };

  return { loading, sessionList, fetchQuestion, clearSessionList, updateSessionList, stopAnswer };
};
